import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { 
  MessageCircle, 
  Send, 
  ArrowLeft, 
  Mail, 
  Clock, 
  User,
  Loader2,
  RefreshCw,
  Filter,
  Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function AdminMessages() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, total: 0 });

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/messages/admin/all?status=${filter}&page=${pagination.page}&limit=20`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        setPagination(prev => ({ ...prev, total: data.total }));
        setUnreadCount(data.total_unread || 0);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/admin/conversation/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setSelectedUser(data.user || null);
        // Refresh unread count after viewing
        fetchConversations();
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendReply = async () => {
    if (!replyContent.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/messages/admin/reply/${selectedConversation}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ content: replyContent, subject: '' })
      });
      
      if (response.ok) {
        toast.success('Reply sent! User will be notified via email.');
        setReplyContent('');
        fetchMessages(selectedConversation);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send reply');
      }
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [filter, pagination.page]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
    }
  }, [selectedConversation]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {selectedConversation && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setSelectedConversation(null);
                setMessages([]);
                setSelectedUser(null);
              }}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Inbox className="w-5 h-5 text-primary" />
              Support Messages
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-2">{unreadCount} unread</Badge>
              )}
            </h2>
            <p className="text-sm text-muted-foreground">
              {selectedConversation ? (selectedUser?.email || 'Conversation') : 'Manage user messages'}
            </p>
          </div>
        </div>
        
        {!selectedConversation && (
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-36">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Messages</SelectItem>
                <SelectItem value="unread">Unread Only</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchConversations}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Conversations List */}
      {!selectedConversation && (
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : conversations.length > 0 ? (
            <>
              {conversations.map((conv) => (
                <Card 
                  key={conv.conversation_id}
                  className={`cursor-pointer hover:border-primary/50 transition-colors ${conv.unread_count > 0 ? 'border-primary/30 bg-primary/5' : ''}`}
                  onClick={() => setSelectedConversation(conv.conversation_id)}
                  data-testid={`admin-conversation-${conv.conversation_id}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium truncate">{conv.subject}</h3>
                          {conv.unread_count > 0 && (
                            <Badge variant="destructive" className="text-xs">{conv.unread_count}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <User className="w-3 h-3 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">{conv.sender_name}</span>
                          <span className="text-xs text-muted-foreground">({conv.sender_email})</span>
                        </div>
                        <p className="text-sm text-muted-foreground truncate mt-1">{conv.last_message}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {new Date(conv.updated_at).toLocaleString()}
                          <span className="mx-1">•</span>
                          <span>{conv.message_count} messages</span>
                        </div>
                      </div>
                      <Mail className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* Pagination */}
              {pagination.total > 20 && (
                <div className="flex justify-center gap-2 pt-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground py-2">
                    Page {pagination.page}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={conversations.length < 20}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Card className="bg-card/50">
              <CardContent className="py-12 text-center">
                <Inbox className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No messages yet</p>
                <p className="text-sm text-muted-foreground mt-1">User messages will appear here</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Conversation View */}
      {selectedConversation && (
        <div className="space-y-4">
          {/* User Info Card */}
          {selectedUser && (
            <Card className="bg-blue-500/5 border-blue-500/20">
              <CardContent className="py-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium">{selectedUser.name || 'Unknown User'}</p>
                    <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                  </div>
                  {selectedUser.is_subscriber && (
                    <Badge className="bg-green-500/20 text-green-400 ml-auto">Subscriber</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Messages */}
          <Card className="bg-card/50">
            <CardContent className="py-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <div className="space-y-4 max-h-96 overflow-y-auto mb-4 p-2">
                    {messages.map((msg) => (
                      <div 
                        key={msg.message_id}
                        className={`p-4 rounded-lg ${
                          msg.sender_type === 'admin' 
                            ? 'bg-primary/10 border-l-4 border-primary ml-8' 
                            : 'bg-slate-800/50 mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`font-medium text-sm ${msg.sender_type === 'admin' ? 'text-primary' : ''}`}>
                            {msg.sender_type === 'admin' ? 'You (Support)' : msg.sender_name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(msg.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Reply Box */}
                  <div className="border-t pt-4">
                    <Label className="text-sm text-muted-foreground mb-2 block">Reply to user</Label>
                    <Textarea
                      placeholder="Type your reply..."
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      rows={3}
                      className="mb-3"
                      data-testid="admin-reply-textarea"
                    />
                    <Button 
                      onClick={sendReply} 
                      disabled={sending || !replyContent.trim()}
                      data-testid="admin-send-reply-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Reply
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      User will receive an email notification when you reply.
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default AdminMessages;
